import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import QRCode from 'qrcode';
import { useAuth } from '../auth/AuthContext';
import { apiMfaConfirm, apiMfaSetup, apiSmsFactor, ApiError } from '../lib/api';
import { Alert, Button, Card, Field, LoadingBlock, TextInput } from '../components/ui';
import { homeFor } from '../lib/portal';
import { PageTitle } from '../components/FolderArt';

/**
 * Enrolling an authenticator app.
 *
 * Reached two ways: a platform user who signed in without one is sent here
 * by the first Control Centre request that answers 403 mfa_enrolment_required,
 * and anyone may come here from their settings. Either way the shape is the
 * same — scan, type one code, done — and the one code is the point: a secret
 * shown but never confirmed is a secret in nobody's app, and challenging a
 * login with it would lock the person out. Nothing is trusted until the app
 * proves it holds the secret.
 *
 * On confirmation the server returns a fresh session that carries the
 * second-factor proof, and it replaces the one that could not get past the
 * Control Centre's door. No second sign-in.
 */
export function TwoFactorSetup() {
  const { session, adoptSession, logout } = useAuth();
  const navigate = useNavigate();
  const [setup, setSetup] = useState<{ secret: string; otpauthUrl: string } | null>(null);
  const [qr, setQr] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // The other way: a six-digit code by text message, for people with no authenticator app.
  const [smsTo, setSmsTo] = useState<string | null>(null);
  const [smsCode, setSmsCode] = useState('');
  const [smsError, setSmsError] = useState<string | null>(null);
  const [smsBusy, setSmsBusy] = useState(false);
  const [smsOn, setSmsOn] = useState(false);

  const smsMessage = (e: unknown) => e instanceof ApiError
    ? (e.code === 'invalid_mfa_code' ? 'That code is not valid. Check the latest message and try again.' : e.message)
    : 'Something went wrong. Please try again.';

  /** Step one: text a code to the mobile number on the profile. Nothing is switched on yet. */
  const sendSms = async () => {
    setSmsError(null); setSmsBusy(true);
    try {
      const r = await apiSmsFactor({ enabled: true });
      setSmsTo(r.to ?? 'your phone'); setSmsCode('');
    } catch (e) { setSmsError(smsMessage(e)); }
    finally { setSmsBusy(false); }
  };
  /** Step two: the code proves the person can read that number, and only then is it trusted. */
  const confirmSms = async (e: FormEvent) => {
    e.preventDefault();
    setSmsError(null); setSmsBusy(true);
    try {
      await apiSmsFactor({ enabled: true, code: smsCode.trim() });
      setSmsOn(true);
    } catch (err) { setSmsError(smsMessage(err)); }
    finally { setSmsBusy(false); }
  };
  const signInAgain = async () => { await logout(); navigate('/login', { replace: true }); };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const s = await apiMfaSetup();
        if (cancelled) return;
        setSetup(s);
        // Rendered client-side from the otpauth URL: the secret never leaves
        // the browser as an image request to anyone.
        setQr(await QRCode.toDataURL(s.otpauthUrl, { width: 220, margin: 1 }));
      } catch (e) {
        if (cancelled) return;
        setError(e instanceof ApiError && e.code === 'mfa_already_enrolled'
          ? 'Two-factor authentication is already set up on this account.'
          : 'Could not start setup. Please try again.');
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const confirm = async (e: FormEvent) => {
    e.preventDefault();
    setError(null); setBusy(true);
    try {
      const s = await apiMfaConfirm(code.trim());
      adoptSession(s);
      navigate(homeFor(s.roles), { replace: true });
    } catch (err) {
      setError(err instanceof ApiError && err.code === 'invalid_mfa_code'
        ? 'That code is not valid. Check the app and try the current code.'
        : 'Could not confirm. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="page" style={{ maxWidth: 560, margin: '0 auto' }}>
      <div className="page-head">
        <PageTitle art="settings" title="Set up two-factor authentication">{session?.roles.some(r => r.endsWith('_admin'))
              ? 'The Control Centre needs a second factor. This takes about a minute.'
              : 'Add a second factor to your sign-in. This takes about a minute.'}</PageTitle>
      </div>

      {error && <div style={{ marginBottom: 16 }}><Alert tone="danger">{error}</Alert></div>}

      <Card title="1. Scan with an authenticator app">
        <p className="page-sub" style={{ marginTop: 0 }}>
          Google Authenticator, Microsoft Authenticator, Authy or any app that reads
          time-based codes. Scan the code, or type the key by hand.
        </p>
        {!setup ? <LoadingBlock /> : (
          <div className="row" style={{ gap: 24, alignItems: 'flex-start', flexWrap: 'wrap' }}>
            {qr && <img src={qr} alt="QR code for your authenticator app" width={220} height={220}
              style={{ borderRadius: 8, background: '#fff', padding: 8 }} />}
            <div style={{ minWidth: 240 }}>
              <div className="muted" style={{ marginBottom: 4 }}>Or enter this key</div>
              <code style={{ wordBreak: 'break-all', fontSize: 15, letterSpacing: 1 }}>
                {setup.secret.replace(/(.{4})/g, '$1 ').trim()}
              </code>
              <div className="muted" style={{ marginTop: 8 }}>Account: NyumbaSuite · Type: time-based</div>
            </div>
          </div>
        )}
      </Card>

      <div style={{ marginTop: 16 }}>
        <Card title="2. Enter the code the app shows">
          <form onSubmit={confirm} noValidate>
            <Field label="Six-digit code" htmlFor="mfa-code"
              hint="Codes change every 30 seconds. Enter the one showing now.">
              <TextInput id="mfa-code" value={code} inputMode="numeric" pattern="[0-9]*" maxLength={6}
                autoComplete="one-time-code" disabled={!setup}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} />
            </Field>
            <Button type="submit" loading={busy} disabled={!setup || code.length !== 6}>
              Confirm and continue
            </Button>
          </form>
        </Card>
      </div>

      <div style={{ marginTop: 16 }}>
        <Card title="Or: get a code by text message">
          {smsOn ? (
            <>
              <Alert tone="success">Text-message codes are on. From your next sign-in you will be asked for a code we text to {smsTo}.</Alert>
              <div style={{ marginTop: 12 }}><Button type="button" onClick={signInAgain}>Sign in again</Button></div>
            </>
          ) : (
            <>
              <p className="page-sub" style={{ marginTop: 0 }}>
                No authenticator app? We can text a six-digit code to the mobile number on your profile each time you
                sign in. Nothing to install.
              </p>
              {smsError && <div style={{ marginBottom: 12 }}><Alert tone="danger">{smsError}</Alert></div>}
              {!smsTo ? (
                <Button type="button" variant="secondary" loading={smsBusy} onClick={sendSms}>Text me a code</Button>
              ) : (
                <form onSubmit={confirmSms} noValidate>
                  <Field label="Six-digit code" htmlFor="sms-code"
                    hint={`We texted a code to ${smsTo}. Enter it to switch text-message codes on.`}>
                    <TextInput id="sms-code" value={smsCode} inputMode="numeric" pattern="[0-9]*" maxLength={6}
                      autoComplete="one-time-code" onChange={(e) => setSmsCode(e.target.value.replace(/\D/g, ''))} />
                  </Field>
                  <div className="row" style={{ gap: 8 }}>
                    <Button type="submit" loading={smsBusy} disabled={smsCode.length !== 6}>Turn it on</Button>
                    <Button type="button" variant="ghost" loading={smsBusy} onClick={sendSms}>Send another</Button>
                  </div>
                </form>
              )}
            </>
          )}
        </Card>
      </div>

      <p className="muted" style={{ marginTop: 16 }}>
        Keep the app on a phone you control. If you lose it, an administrator can reset
        two-factor for your account.
      </p>
    </div>
  );
}
